import { useTranslation } from 'react-i18next';

import { TabsList, TabsTrigger } from '@/components/ui/tabs';

import type { NotificationIconName } from '../notification-icons';
import { NOTIFICATION_TABS, type NotificationType } from '../types';
import { CountChip } from './CountChip';
import { NotificationIcon } from './NotificationIcon';

interface NotificationTabsListProps {
    messageUnread: number;
    alertUnread: number;
}

/**
 * The tab glyphs echo the rows beneath them — same family, same weight. They
 * are glyph NAMES rather than components for the same reason the category map
 * uses names: `NotificationIcon` is the one place that resolves them.
 */
const TAB_META: Record<
    NotificationType,
    { icon: NotificationIconName; labelKey: string; fallback: string }
> = {
    MESSAGE: {
        icon: 'MessageCircleMore',
        labelKey: 'notifications.tabs.messages',
        fallback: 'Messages',
    },
    ALERT: { icon: 'Bell', labelKey: 'notifications.tabs.alerts', fallback: 'Alerts' },
};

/**
 * The Messages / Alerts strip, shared by the bell popover and the "View all"
 * drawer so the two can never drift apart.
 */
export function NotificationTabsList({ messageUnread, alertUnread }: NotificationTabsListProps) {
    const { t } = useTranslation();

    return (
        <TabsList className="w-full p-0.75">
            {NOTIFICATION_TABS.map((tab) => {
                const { icon, labelKey, fallback } = TAB_META[tab];
                return (
                    <TabsTrigger key={tab} value={tab}>
                        <NotificationIcon name={icon} className="size-4" />
                        {t(labelKey, fallback)}
                        <CountChip count={tab === 'MESSAGE' ? messageUnread : alertUnread} />
                    </TabsTrigger>
                );
            })}
        </TabsList>
    );
}
